import { useState, useEffect, useCallback, useRef } from "react";
import "./OverviewTab.css";
import { useLatido } from "../../../../../hooks/useLatido";
import {
  RadarChart, Radar, PolarGrid, PolarAngleAxis,
  ResponsiveContainer, AreaChart, Area, XAxis, YAxis,
  CartesianGrid, Tooltip
} from "recharts";
import {
  fetchGlobalKpis,
  fetchStudentList,
  fetchStudentDetail,
  fetchSpacySessions,
  fetchArenaSessions,
} from "./adminService";
import LiveUsersPanel from "./LiveUsersPanel";  // 🔥 IMPORTADO

import StatsGrid from "../../../../dashboard/StatsGrid";
import ActivityChartCard from "../../../../dashboard/ActivityChartCard";
import SkillsRadarChart from "../../../../dashboard/SkillsRadarChart";
import StudyTimeDoughnutChart from "../../../../dashboard/StudyTimeDoughnutChart";

// 🔥 MAGIC VARIABLE PARA BASE URL (corrige fallos en producción)
const API_BASE = import.meta.env.VITE_API_URL || '';

function useBreak() {
  const [w, setW] = useState(typeof window !== "undefined" ? window.innerWidth : 1400);
  useEffect(() => {
    const fn = () => setW(window.innerWidth);
    window.addEventListener("resize", fn);
    return () => window.removeEventListener("resize", fn);
  }, []);
  return { w, xs: w < 480, sm: w < 640, md: w < 1024, lg: w >= 1024 };
}

const I = {
  Flame: (p) => <svg width={p.s || 16} height={p.s || 16} viewBox="0 0 24 24" fill="none" stroke={p.c || "currentColor"} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M8.5 14.5A2.5 2.5 0 0 0 11 12c0-1.38-.5-2-1-3-1.072-2.143-.224-4.054 2-6 .5 2.5 2 4.9 4 6.5 2 1.6 3 3.5 3 5.5a7 7 0 1 1-14 0c0-1.153.433-2.294 1-3a2.5 2.5 0 0 0 2.5 2.5z" /></svg>,
  Zap: (p) => <svg width={p.s || 16} height={p.s || 16} viewBox="0 0 24 24" fill="none" stroke={p.c || "currentColor"} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M4 14a1 1 0 0 1-.78-1.63l9.9-10.2a.5.5 0 0 1 .86.46l-1.92 6.02A1 1 0 0 0 13 10h7a1 1 0 0 1 .78 1.63l-9.9 10.2a.5.5 0 0 1-.86-.46l1.92-6.02A1 1 0 0 0 11 14z" /></svg>,
  XCircle: (p) => <svg width={p.s || 16} height={p.s || 16} viewBox="0 0 24 24" fill="none" stroke={p.c || "currentColor"} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10" /><path d="m15 9-6 6" /><path d="m9 9 6 6" /></svg>,
  Cpu: (p) => <svg width={p.s || 16} height={p.s || 16} viewBox="0 0 24 24" fill="none" stroke={p.c || "currentColor"} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect width="16" height="16" x="4" y="4" rx="2" /><rect width="6" height="6" x="9" y="9" rx="1" /><path d="M15 2v2M15 20v2M2 15h2M2 9h2M20 15h2M20 9h2M9 2v2M9 20v2" /></svg>,
  Sparkles: (p) => <svg width={p.s || 16} height={p.s || 16} viewBox="0 0 24 24" fill="none" stroke={p.c || "currentColor"} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="m12 3-1.912 5.813a2 2 0 0 1-1.275 1.275L3 12l5.813 1.912a2 2 0 0 1 1.275 1.275L12 21l1.912-5.813a2 2 0 0 1 1.275-1.275L21 12l-5.813-1.912a2 2 0 0 1-1.275-1.275L12 3Z" /><path d="M5 3v4M19 17v4M3 5h4M17 19h4" /></svg>,
  Book: (p) => <svg width={p.s || 16} height={p.s || 16} viewBox="0 0 24 24" fill="none" stroke={p.c || "currentColor"} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M2 3h6a4 4 0 0 1 4 4v14a3 3 0 0 0-3-3H2z" /><path d="M22 3h-6a4 4 0 0 0-4 4v14a3 3 0 0 1 3-3h7z" /></svg>,
  Menu: (p) => <svg width={p.s || 16} height={p.s || 16} viewBox="0 0 24 24" fill="none" stroke={p.c || "currentColor"} strokeWidth="2" strokeLinecap="round"><line x1="3" y1="6" x2="21" y2="6" /><line x1="3" y1="12" x2="21" y2="12" /><line x1="3" y1="18" x2="21" y2="18" /></svg>,
  Close: (p) => <svg width={p.s || 16} height={p.s || 16} viewBox="0 0 24 24" fill="none" stroke={p.c || "currentColor"} strokeWidth="2" strokeLinecap="round"><path d="m18 6-12 12" /><path d="m6 6 12 12" /></svg>,
};

const F = {
  "2xs": 10, xs: 12, sm: 13, md: 14, lg: 15, xl: 20, "2xl": 24, "3xl": 34, "4xl": 42,
};

/* ── PRIMITIVES ── */
function Ring({ pct, size = 72, stroke = 6, color, bg, glow, children }) {
  const r = (size - stroke) / 2, circ = 2 * Math.PI * r;
  const dash = (Math.min(100, pct) / 100) * circ;
  return (
    <div style={{ position: "relative", width: size, height: size, flexShrink: 0 }}>
      <svg width={size} height={size} style={{ transform: "rotate(-90deg)" }}>
        <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke={bg || "rgba(255,255,255,.06)"} strokeWidth={stroke} />
        <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke={color} strokeWidth={stroke}
          strokeDasharray={`${dash} ${circ}`} strokeLinecap="round"
          style={{ filter: glow ? `drop-shadow(0 0 5px ${color})` : "none", transition: "stroke-dasharray .9s ease" }} />
      </svg>
      <div style={{ position: "absolute", inset: 0, display: "flex", alignItems: "center", justifyContent: "center" }}>{children}</div>
    </div>
  );
}

function Bar({ v, c, h = 5, t }) {
  return (
    <div style={{ height: h, borderRadius: 3, overflow: "hidden", background: t.dark ? "rgba(255,255,255,.07)" : "rgba(0,0,0,.07)" }}>
      <div style={{ width: `${Math.min(100, v)}%`, height: "100%", borderRadius: 3, background: c, transition: "width .7s ease" }} />
    </div>
  );
}

function Bdg({ label, color, bg, mono }) {
  return <span style={{ fontSize: F.xs, fontWeight: 700, padding: "3px 9px", borderRadius: 50, background: bg || `${color}14`, color, border: `1px solid ${color}22`, whiteSpace: "nowrap", fontFamily: mono ? "JetBrains Mono,monospace" : "inherit" }}>{label}</span>;
}

function KpiMini({ icon, label, value, color, t, dark, sub }) {
  return (
    <div style={{ background: t.bg, borderRadius: 14, padding: "10px 12px", boxShadow: t.nsm, border: `1px solid ${dark ? "rgba(255,255,255,.06)" : "rgba(0,0,0,.06)"}`, position: "relative", overflow: "hidden" }}>
      <div style={{ position: "absolute", top: -8, right: -8, width: 32, height: 32, borderRadius: "50%", background: color, opacity: .07, filter: "blur(10px)" }} />
      <div style={{ fontSize: F.lg, marginBottom: 3 }}>{icon}</div>
      <div style={{ fontSize: F.xl, fontWeight: 900, color, fontFamily: "monospace", lineHeight: 1, textShadow: dark ? `0 0 10px ${color}44` : "" }}>{value}</div>
      <div style={{ fontSize: F.xs, color: t.tx2, textTransform: "uppercase", letterSpacing: ".07em", marginTop: 3 }}>{label}</div>
      {sub && (
        <div style={{ fontSize: F.xs, color: "#ef4444", fontWeight: 700, marginTop: 2, whiteSpace: "nowrap" }}>
          {sub}
        </div>
      )}
    </div>
  );
}

/* ── HELPERS ── */
const levelColor = lv => ({ A1: "#64748b", A2: "#6366f1", B1: "#0891b2", B2: "#059669", C1: "#f59e0b", C2: "#ef4444" }[lv] || "#6366f1");
const riskCfg = { low: { c: "#10b981", l: "Bajo" }, medium: { c: "#f59e0b", l: "Medio" }, high: { c: "#f97316", l: "Alto" }, critical: { c: "#ef4444", l: "Crítico" } };
const topicCfg = { mastered: { c: "#10b981", icon: "✅", l: "Dominado" }, in_progress: { c: "#f59e0b", icon: "▶", l: "En curso" }, failed: { c: "#ef4444", icon: "✕", l: "Falló" }, not_started: { c: "#64748b", icon: "○", l: "Sin iniciar" }, completed: { c: "#10b981", icon: "✅", l: "Completado" } };

// Las claves tienen que ser LAS MISMAS que devuelve /api/admin/users/kpis. Aqui
// habia `levelUpThisMonth`, que el servidor no manda nunca, asi que ese recuadro
// salia en blanco — y en blanco no parece un fallo, parece que no hay dato.
const GLOBAL_EMPTY = { totalStudents: 0, otherAccounts: {}, activeToday: 0, avgFluency: null, interventions: 0, mrrTotal: "$0", mrrEnRiesgo: "$0", convHours: "0", churnRisk: 0, newStudentsThisMonth: 0 };

/* ── SPACY MSG BUBBLE ── */
function SpacyMsgBubble({ msg, HI, t, dark }) {
  const [esOpen, setEsOpen] = useState(false);
  const isStudent = msg.speaker === "Student";
  const hasSep = !isStudent && msg.text.includes("---");
  const enText = hasSep ? msg.text.split("---")[0].trim() : msg.text;
  const esText = hasSep ? msg.text.split("---")[1].trim() : null;
  const BADGE_MAP = {
    "**Vocabulary Alert**:": { lbl: "📚 Vocab", bg: dark ? "#5d4037" : "#ffccbc", tc: dark ? "#ffeb3b" : "#d84315" },
    "**Pro Tip**:": { lbl: "💡 Pro Tip", bg: dark ? "#1b5e20" : "#c8e6c9", tc: dark ? "#a5d6a7" : "#2e7d32" },
    "**Example**:": { lbl: "📝 Example", bg: dark ? "#0d47a1" : "#bbdefb", tc: dark ? "#90caf9" : "#1565c0" },
    "**Correction**:": { lbl: "✏️ Fix", bg: dark ? "#bf360c" : "#ffccbc", tc: dark ? "#ffab91" : "#d84315" },
    "**Better**:": { lbl: "✏️ Better", bg: dark ? "#bf360c" : "#ffccbc", tc: dark ? "#ffab91" : "#d84315" },
  };
  const renderLine = (line, idx) => {
    for (const [marker, cfg] of Object.entries(BADGE_MAP)) {
      if (line.startsWith(marker)) {
        const rest = line.slice(marker.length).trim();
        const parts = rest.split(/(\*\*[^*]+\*\*)/g);
        return (
          <div key={idx} style={{ display: "flex", alignItems: "flex-start", gap: 7, marginBottom: 5 }}>
            <span style={{ fontSize: F.xs, fontWeight: 700, padding: "3px 8px", borderRadius: 50, background: cfg.bg, color: cfg.tc, whiteSpace: "nowrap", flexShrink: 0, marginTop: 2 }}>{cfg.lbl}</span>
            <span style={{ fontSize: F.sm, color: t.tx, lineHeight: 1.5 }}>{parts.map((p, pi) => p.startsWith("**") && p.endsWith("**") ? <strong key={pi} style={{ fontWeight: 700, background: dark ? "rgba(255,255,0,.18)" : "rgba(255,255,0,.6)", borderRadius: 3, padding: "0 2px" }}>{p.slice(2, -2)}</strong> : <span key={pi}>{p}</span>)}</span>
          </div>
        );
      }
    }
    const parts = line.split(/(\*\*[^*]+\*\*)/g);
    return <p key={idx} style={{ margin: "0 0 5px", fontSize: F.sm, color: t.tx, lineHeight: 1.6 }}>{parts.map((p, pi) => p.startsWith("**") && p.endsWith("**") ? <strong key={pi} style={{ fontWeight: 700, background: dark ? "rgba(255,255,0,.18)" : "rgba(255,255,0,.6)", borderRadius: 3, padding: "0 2px" }}>{p.slice(2, -2)}</strong> : <span key={pi}>{p}</span>)}</p>;
  };
  return (
    <div style={{ display: "flex", justifyContent: isStudent ? "flex-end" : "flex-start" }}>
      <div style={{ maxWidth: "88%", padding: "10px 13px", borderRadius: isStudent ? "12px 12px 3px 12px" : "12px 12px 12px 3px", background: isStudent ? (dark ? `${HI}20` : `${HI}14`) : (dark ? "rgba(255,255,255,.05)" : "rgba(0,0,0,.03)"), border: `1px solid ${isStudent ? HI + "33" : "transparent"}` }}>
        {!isStudent && <div style={{ fontSize: F["2xs"], color: HI, fontWeight: 800, letterSpacing: ".1em", textTransform: "uppercase", marginBottom: 6, display: "flex", alignItems: "center", gap: 4 }}><span style={{ width: 14, height: 14, borderRadius: "50%", fontSize: 9, background: `linear-gradient(135deg,${HI},#c084fc)`, display: "inline-flex", alignItems: "center", justifyContent: "center" }}>🤖</span>SpaCy</div>}
        <div>{enText.split("\n").map((line, li) => line.trim() ? renderLine(line.trim(), li) : <div key={li} style={{ height: 4 }} />)}</div>
        {esText && (
          <div style={{ marginTop: 7, borderTop: `1px solid ${dark ? "rgba(255,255,255,.07)" : "rgba(0,0,0,.07)"}`, paddingTop: 6 }}>
            <button onClick={e => { e.stopPropagation(); setEsOpen(o => !o); }} style={{ background: "transparent", border: "none", cursor: "pointer", fontSize: F.xs, color: t.tx2, display: "flex", alignItems: "center", gap: 4, padding: "2px 0", fontFamily: "inherit" }}>🇪🇸 {esOpen ? "Ocultar" : "Ver"} traducción {esOpen ? "▲" : "▼"}</button>
            {esOpen && <div style={{ marginTop: 6, paddingLeft: 9, borderLeft: "2px solid rgba(0,255,136,.4)", fontSize: F.sm, color: t.tx2, fontStyle: "italic", lineHeight: 1.5 }}>{esText.split("\n").map((l, li) => l.trim() ? <p key={li} style={{ margin: "0 0 3px" }}>{l}</p> : null)}</div>}
          </div>
        )}
      </div>
    </div>
  );
}

/* ── SESSION ROW ── */
function SessionRow({ s, t, HI, dark }) {
  const [isExp, setIsExp] = useState(false);
  const confC = s.conf >= 80 ? "#10b981" : s.conf >= 60 ? (dark ? "#00FFFF" : "#6366f1") : s.conf >= 40 ? "#f59e0b" : "#ef4444";
  const dateStr = s.at instanceof Date
    ? s.at.toLocaleDateString("es-MX", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" })
    : String(s.at);
  return (
    <div>
      <div onClick={() => setIsExp(p => !p)} style={{ display: "flex", alignItems: "center", gap: 10, padding: "10px 12px", borderRadius: isExp ? "12px 12px 0 0" : 12, background: isExp ? `${HI}09` : (dark ? "rgba(255,255,255,.03)" : "rgba(0,0,0,.025)"), boxShadow: t.ixs, cursor: "pointer", borderLeft: `3px solid ${confC}` }}>
        <div style={{ position: "relative", width: 34, height: 34, flexShrink: 0 }}>
          <svg width={34} height={34} style={{ transform: "rotate(-90deg)" }}>
            <circle cx={17} cy={17} r={14} fill="none" stroke={dark ? "rgba(255,255,255,.05)" : "rgba(0,0,0,.05)"} strokeWidth={2.5} />
            <circle cx={17} cy={17} r={14} fill="none" stroke={confC} strokeWidth={2.5} strokeDasharray={`${(Math.min(100, s.conf) / 100) * 2 * Math.PI * 14} ${2 * Math.PI * 14}`} strokeLinecap="round" />
          </svg>
          <div style={{ position: "absolute", inset: 0, display: "flex", alignItems: "center", justifyContent: "center" }}><span style={{ fontSize: F.xs, fontWeight: 900, fontFamily: "monospace", color: confC }}>{s.conf}</span></div>
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: F.sm, fontWeight: 600, color: t.tx, lineHeight: 1.3, marginBottom: 3, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{s.summary}</div>
          <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
            <span style={{ fontSize: F.xs, color: t.tx2 }}>{dateStr}</span>
            <span style={{ fontSize: F.xs, fontWeight: 700, color: "#10b981" }}>⏱{Math.round(s.dur / 60 * 10) / 10}m</span>
            {s.errors.length > 0 && <span style={{ fontSize: F.xs, fontWeight: 700, color: "#f59e0b" }}>⚠{s.errors.length}</span>}
          </div>
        </div>
        <span style={{ fontSize: F.sm, color: t.tx2 }}>{isExp ? "▲" : "▼"}</span>
      </div>
      {isExp && (
        <div style={{ background: dark ? "rgba(255,255,255,.03)" : "rgba(0,0,0,.02)", borderRadius: "0 0 12px 12px", padding: "12px 14px", border: `1px solid ${HI}18`, borderTop: "none", animation: "fadeUp .2s ease" }}>
          {s.errors.length > 0 && <div style={{ marginBottom: 9 }}>
            {s.errors.map((e, ei) => <div key={ei} style={{ display: "flex", alignItems: "center", gap: 7, padding: "4px 10px", borderRadius: 8, marginBottom: 4, background: "rgba(239,68,68,.07)", border: "1px solid rgba(239,68,68,.2)" }}><span style={{ fontSize: F.xs, fontWeight: 700, color: "#ef4444" }}>{e.t}</span><span style={{ fontSize: F.sm, color: t.tx, fontFamily: "monospace" }}>"{e.tx}"</span></div>)}
          </div>}
          <div style={{ fontSize: F.xs, fontWeight: 800, textTransform: "uppercase", letterSpacing: ".1em", color: t.tx2, marginBottom: 8 }}>Conversación</div>
          <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>{s.msgs.map((msg, mi) => <SpacyMsgBubble key={mi} msg={msg} HI={HI} t={t} dark={dark} />)}</div>
        </div>
      )}
    </div>
  );
}

/* ── SPACY SESSION PANEL ── */
/* == LA ARENA ================================================================
   Se enseña distinto a proposito, porque mide algo distinto.

   Una leccion tiene nota; la Arena NO pone nota, y esa decision ya se tomo: es
   el examen extra, el bonus. Lo que un profesor quiere saber aqui son tres
   cosas, y ninguna es un numero del 1 al 10:

     - Logro la mision? Lo unico binario que hay. Viene del juez que corre al
       cerrar el combate; antes se calculaba, se enseñaba una vez y se tiraba.
     - Estuvo en INGLES? La inmersion es LA medida de este ejercicio. Se pueden
       aguantar veinte turnos contestando en español, y eso no es haber
       practicado. Por eso al lado van las palabras de cada idioma: un 60% suena
       bien hasta que ves que fueron seis palabras en total.
     - Aguanto? Turnos y minutos. Una mision de dos turnos es una mision
       abandonada, por mucho que la inmersion salga del 100%.
============================================================================ */
function ArenaPanel({ combates, t, HI, dark }) {
  const logrados = combates.filter(c => c.logrado).length;
  const conVeredicto = combates.filter(c => !c.parcial).length;
  const inmMedia = combates.length
    ? Math.round(combates.reduce((a, c) => a + (c.inmersion || 0), 0) / combates.length)
    : 0;
  const minutos = Math.round(combates.reduce((a, c) => a + (c.dur || 0), 0) / 60);
  const sep2 = dark ? "rgba(255,255,255,.06)" : "rgba(0,0,0,.06)";
  const colorInm = v => (v >= 80 ? "#10b981" : v >= 55 ? "#f59e0b" : "#ef4444");

  const METRICAS = [
    { l: "Combates", v: combates.length, c: HI, icon: "🥊" },
    // Sobre los que TIENEN veredicto. Poner "3/21" contando sesiones antiguas
    // que nunca tuvieron juez seria inventarle al alumno derrotas que no tuvo.
    { l: "Logrados", v: conVeredicto ? `${logrados}/${conVeredicto}` : "—",
      c: logrados > 0 ? "#10b981" : t.tx2, icon: "🏁" },
    { l: "Inmersión", v: `${inmMedia}%`, c: colorInm(inmMedia), icon: "🌐" },
    { l: "Minutos", v: `${minutos}m`, c: "#c084fc", icon: "⏱" },
  ];

  return (
    <>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(4,1fr)", gap: 8 }}>
        {METRICAS.map(m => (
          <div key={m.l} style={{ background: t.bg, borderRadius: 12, padding: "9px 10px", boxShadow: t.nsm, border: `1px solid ${sep2}` }}>
            <div style={{ fontSize: F.md }}>{m.icon}</div>
            <div style={{ fontSize: F.lg, fontWeight: 900, color: m.c, fontFamily: "monospace", lineHeight: 1.1 }}>{m.v}</div>
            <div style={{ fontSize: F.xs, color: t.tx2, textTransform: "uppercase", letterSpacing: ".06em", marginTop: 2 }}>{m.l}</div>
          </div>
        ))}
      </div>

      <div style={{ display: "flex", flexDirection: "column", gap: 8, marginTop: 10 }}>
        {combates.map(c => (
          <div key={c.id} style={{ background: t.bg, borderRadius: 12, padding: "11px 13px", boxShadow: t.nsm, border: `1px solid ${sep2}` }}>
            <div style={{ display: "flex", alignItems: "flex-start", gap: 8, justifyContent: "space-between", flexWrap: "wrap" }}>
              <div style={{ minWidth: 0, flex: 1 }}>
                <div style={{ fontSize: F.sm, fontWeight: 700, color: t.tx, lineHeight: 1.35 }}>
                  {c.personaje || "Escenario sin registrar"}
                </div>
                <div style={{ fontSize: F.xs, color: t.tx2, marginTop: 3 }}>
                  {c.at ? new Date(c.at).toLocaleString("es-MX", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" }) : "—"}
                  {" · "}{c.turnos || 0} turnos · {Math.round((c.dur || 0) / 60)}m · {c.wpm || 0} ppm
                </div>
              </div>
              <div style={{ display: "flex", gap: 6, alignItems: "center", flexShrink: 0 }}>
                <span style={{ fontSize: F.xs, fontWeight: 800, padding: "3px 9px", borderRadius: 50,
                  background: colorInm(c.inmersion) + "1a", color: colorInm(c.inmersion) }}>
                  {c.inmersion}% inglés
                </span>
                {/* Un combate migrado no tiene veredicto. Marcarlo como "no
                    logrado" seria colgarle una derrota que nunca se juzgo. */}
                {c.parcial
                  ? <span style={{ fontSize: F.xs, fontWeight: 700, padding: "3px 9px", borderRadius: 50, background: dark ? "rgba(255,255,255,.06)" : "rgba(0,0,0,.05)", color: t.tx2 }}>sin juzgar</span>
                  : <span style={{ fontSize: F.xs, fontWeight: 800, padding: "3px 9px", borderRadius: 50,
                      background: (c.logrado ? "#10b981" : "#ef4444") + "1a",
                      color: c.logrado ? "#10b981" : "#ef4444" }}>
                      {c.logrado ? "✓ logrado" : "✕ no logrado"}
                    </span>}
              </div>
            </div>

            <div style={{ fontSize: F.xs, color: t.tx2, marginTop: 6 }}>
              {c.ingles} palabras en inglés · {c.espanol} en español
            </div>

            {/* Lo que el juez le dijo AL ALUMNO, palabra por palabra. El profesor
                tiene que leer exactamente lo mismo que leyo el. */}
            {(c.porQue || c.error || c.momento) && (
              <div style={{ marginTop: 8, paddingTop: 8, borderTop: `1px solid ${sep2}`, display: "flex", flexDirection: "column", gap: 4 }}>
                {c.porQue && <div style={{ fontSize: F.xs, color: t.tx, lineHeight: 1.5 }}>{c.porQue}</div>}
                {c.error && <div style={{ fontSize: F.xs, color: "#f59e0b", lineHeight: 1.5 }}>⚠ {c.error}</div>}
                {c.momento && <div style={{ fontSize: F.xs, color: "#10b981", lineHeight: 1.5, fontStyle: "italic" }}>{c.momento}</div>}
              </div>
            )}
          </div>
        ))}
      </div>
    </>
  );
}

// sessions: array que llega como prop desde OverviewTab (fetchSpacySessions)
function SpacySessionPanel({ sel, t, HI, dark, sessions = [], arena = [] }) {
  const [sesFilter, setSesFilter] = useState("month");

  // ── LAS TRES CARAS DE SPACY ─────────────────────────────────────────────
  // Hasta ahora esto enseñaba una sola lista con TODO revuelto, y la Arena no
  // salia por ningun lado —no guardaba nada—. Revueltas no se pueden leer:
  // una leccion del curso y una simulacion libre miden cosas distintas, y la
  // Arena mide una tercera que no se parece a ninguna.
  const [fuente, setFuente] = useState("leccion");

  const now2 = new Date();
  const todayStart = new Date(now2); todayStart.setHours(0, 0, 0, 0);
  const weekStart = new Date(now2); weekStart.setDate(weekStart.getDate() - 7);
  const monthStart = new Date(now2.getFullYear(), now2.getMonth(), 1);
  const starts = { day: todayStart, week: weekStart, month: monthStart };

  // Las sesiones del tipo elegido. El servidor marca cada una como "leccion"
  // o "simulacion" segun su lesson_id apunte o no a una leccion real.
  const delTipo = sessions.filter(s => (s.tipo || "leccion") === fuente);

  const visible = delTipo.filter(s => {
    const at = s.at instanceof Date ? s.at : new Date(s.at);
    return at >= starts[sesFilter];
  });

  const arenaVisible = arena.filter(a => {
    const at = a.at instanceof Date ? a.at : new Date(a.at);
    return at >= starts[sesFilter];
  });

  const FUENTES = [
    { k: "leccion",    l: "Lecciones",    e: "📘", n: sessions.filter(x => (x.tipo || "leccion") === "leccion").length },
    { k: "simulacion", l: "Simulaciones", e: "🎭", n: sessions.filter(x => x.tipo === "simulacion").length },
    { k: "arena",      l: "Arena",        e: "🥊", n: arena.length },
  ];

  const totalMins = Math.round(visible.reduce((a, s) => a + s.dur, 0) / 60);
  const avgConf = visible.length ? Math.round(visible.reduce((a, s) => a + s.conf, 0) / visible.length) : 0;
  const totalErrors = visible.reduce((a, s) => a + s.errors.length, 0);
  const allVocab = [...new Set(visible.flatMap(s => s.vocab))];
  const labelMap = { day: "Hoy", week: "Esta semana", month: "Este mes" };

  if (!sel) return null;

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
      {/* Que Spacy se esta mirando. Va ARRIBA del filtro de fechas porque
          cambia de que se habla, no cuanto se enseña. */}
      <div style={{ background: t.bg, borderRadius: 11, padding: 4, boxShadow: t.ixs, display: "flex", gap: 3, border: `1px solid ${dark ? "rgba(255,255,255,.05)" : "rgba(0,0,0,.05)"}` }}>
        {FUENTES.map(o => {
          const a = fuente === o.k;
          return (
            <button key={o.k} onClick={() => setFuente(o.k)} style={{
              flex: 1, padding: "8px 6px", borderRadius: 9, border: "none", cursor: "pointer",
              fontSize: F.sm, fontWeight: a ? 700 : 500, fontFamily: "inherit",
              background: a ? `${HI}14` : "transparent",
              boxShadow: a ? `${t.nxs},0 0 0 1.5px ${HI}44` : "none",
              color: a ? HI : t.tx2, transition: "all .18s",
              display: "flex", alignItems: "center", justifyContent: "center", gap: 6,
            }}>
              <span>{o.e}</span>{o.l}
              <span style={{ fontSize: F.xs, padding: "1px 6px", borderRadius: 50, background: a ? `${HI}18` : dark ? "rgba(255,255,255,.06)" : "rgba(0,0,0,.05)", color: a ? HI : t.tx2, fontWeight: 800 }}>{o.n}</span>
            </button>
          );
        })}
      </div>

      <div style={{ background: t.bg, borderRadius: 11, padding: 4, boxShadow: t.ixs, display: "flex", gap: 3, border: `1px solid ${dark ? "rgba(255,255,255,.05)" : "rgba(0,0,0,.05)"}` }}>
        {["day", "week", "month"].map(f => {
          const a = sesFilter === f;
          // Cuenta la fuente ELEGIDA. Contando `sessions` entero, el boton
          // decia "12 este mes" y la lista de abajo enseñaba tres.
          const cnt = (fuente === "arena" ? arena : delTipo).filter(s => {
            const at = s.at instanceof Date ? s.at : new Date(s.at);
            return at >= starts[f];
          }).length;
          return <button key={f} onClick={() => setSesFilter(f)} style={{ flex: 1, padding: "8px 6px", borderRadius: 9, border: "none", cursor: "pointer", fontSize: F.sm, fontWeight: a ? 700 : 500, fontFamily: "inherit", background: a ? `${HI}14` : "transparent", boxShadow: a ? `${t.nxs},0 0 0 1.5px ${HI}44` : "none", color: a ? HI : t.tx2, transition: "all .18s", display: "flex", alignItems: "center", justifyContent: "center", gap: 6 }}>{labelMap[f]}<span style={{ fontSize: F.xs, padding: "1px 6px", borderRadius: 50, background: a ? `${HI}18` : dark ? "rgba(255,255,255,.06)" : "rgba(0,0,0,.05)", color: a ? HI : t.tx2, fontWeight: 800 }}>{cnt}</span></button>;
        })}
      </div>

      {fuente === "arena" ? (
        arenaVisible.length === 0
          ? <div style={{ textAlign: "center", padding: "24px 0", color: t.tx2, fontSize: F.md }}>Sin combates {labelMap[sesFilter].toLowerCase()}</div>
          : <ArenaPanel combates={arenaVisible} t={t} HI={HI} dark={dark} />
      ) : visible.length === 0
        ? <div style={{ textAlign: "center", padding: "24px 0", color: t.tx2, fontSize: F.md }}>Sin sesiones {labelMap[sesFilter].toLowerCase()}</div>
        : (
          <>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(4,1fr)", gap: 8 }}>
              {[{ l: "Sesiones", v: visible.length, c: HI, icon: "💬" }, { l: "Minutos", v: `${totalMins}m`, c: "#10b981", icon: "⏱" }, { l: "Confianza", v: `${avgConf}%`, c: "#c084fc", icon: "💡" }, { l: "Errores", v: totalErrors, c: totalErrors === 0 ? "#10b981" : totalErrors < 3 ? "#f59e0b" : "#ef4444", icon: "⚠️" }].map(m => (
                <div key={m.l} style={{ background: t.bg, borderRadius: 11, padding: "8px 10px", boxShadow: t.ixs, textAlign: "center", border: `1px solid ${dark ? "rgba(255,255,255,.04)" : "rgba(0,0,0,.04)"}` }}>
                  <div style={{ fontSize: F.lg, marginBottom: 3 }}>{m.icon}</div>
                  <div style={{ fontSize: F.xl, fontWeight: 900, color: m.c, fontFamily: "monospace", lineHeight: 1, textShadow: dark ? `0 0 6px ${m.c}44` : "" }}>{m.v}</div>
                  <div style={{ fontSize: F.xs, color: t.tx2, marginTop: 3, textTransform: "uppercase", letterSpacing: ".06em" }}>{m.l}</div>
                </div>
              ))}
            </div>

            {allVocab.length > 0 && (
              <div style={{ padding: "10px 12px", borderRadius: 11, background: t.bg, boxShadow: t.ixs, border: `1px solid ${dark ? "rgba(255,255,255,.04)" : "rgba(0,0,0,.04)"}` }}>
                <div style={{ fontSize: F.xs, color: t.tx2, textTransform: "uppercase", letterSpacing: ".1em", marginBottom: 8, fontWeight: 700 }}>Vocabulario</div>
                <div style={{ display: "flex", flexWrap: "wrap", gap: 5 }}>{allVocab.slice(0, 10).map((w, i) => { const cols = [HI, "#c084fc", "#f472b6", "#10b981", "#f59e0b", "#60a5fa"]; const c = cols[i % cols.length]; return <span key={i} style={{ padding: "3px 9px", borderRadius: 50, fontSize: F.sm, fontWeight: 700, background: `${c}14`, color: c, border: `1px solid ${c}22` }}>{w}</span>; })}</div>
              </div>
            )}

            <div style={{ display: "flex", flexDirection: "column", gap: 6, maxHeight: "75vh", overflowY: "auto", paddingRight: "8px" }}>
              {visible.map(s => <SessionRow key={s.id} s={s} t={t} HI={HI} dark={dark} />)}
            </div>
          </>
        )
      }
    </div>
  );
}

/* ════════════════════════════════════════════════
   MAIN EXPORT
════════════════════════════════════════════════ */
export default function OverviewTab({ t: parentT, HICOLOR, dark }) {
  const t = { ...parentT, pur: dark ? "#c084fc" : "#8b5cf6", pnk: dark ? "#f472b6" : "#ec4899", blu: dark ? "#60a5fa" : "#3b82f6" };
  const HI = HICOLOR;
  const bp = useBreak();

  /* ── API state ── */
  const [students, setStudents] = useState([]);
  const [globalKpis, setGlobalKpis] = useState(GLOBAL_EMPTY);
  const [spacySessions, setSpacySessions] = useState([]);
  const [arenaSessions, setArenaSessions] = useState([]);
  const [loadingList, setLoadingList] = useState(true);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [apiError, setApiError] = useState(null);

  /* ── UI state ── */
  const [sel, setSel] = useState(null);
  const [selectedId, setSelectedId] = useState(null);
  const [q, setQ] = useState("");
  const [fRisk, setFRisk] = useState("all");
  const [fLevel, setFLevel] = useState("all");
  const [sFocus, setSFocus] = useState(false);
  const [hovId, setHovId] = useState(null);
  const [mainTab, setMainTab] = useState("overview");
  const [tabKey, setTabKey] = useState(0);
  const [testRunning, setTR] = useState(false);
  const [testResult, setTRes] = useState(null);
  const [drawerOpen, setDrawerOpen] = useState(false);

  /* ── Radar en Tiempo Real (Live Users) ── */
  const [liveUsers, setLiveUsers] = useState([]);
  // ¿Cambió la lista desde la última vuelta? Lo usa el latido para decidir
  // si sigue a 15 s o afloja.
  const cambioRef = useRef(false);

  // ── QUIÉN ESTÁ EN VIVO · cada 15 s mirando, nada si nadie mira ──────────
  // Este radar preguntaba cada 15 segundos mientras la pestaña existiera. Con
  // el Command Center abierto todo el día eran 5.760 consultas diarias para
  // enseñar una lista que nadie tenía delante (19-09-2026).
  const pollLiveUsers = useCallback(async () => {
    try {
      const token = localStorage.getItem("token");
      if (!token) return false;
      // 🔥 INCISIÓN: fetch con base URL y verificación de respuesta HTML
      const res = await fetch(`${API_BASE}/api/presence/live-users`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const contentType = res.headers.get("content-type");
        if (!contentType || !contentType.includes("application/json")) {
          console.error("Radar Error: La API devolvió HTML en lugar de JSON.");
          return false;
        }
        const data = await res.json();
        const gente = data.users || [];
        setLiveUsers((antes) => {
          cambioRef.current = JSON.stringify(antes.map((u) => u.id || u.email))
            !== JSON.stringify(gente.map((u) => u.id || u.email));
          return gente;
        });
        return cambioRef.current;
      }
    } catch (e) {
      console.debug("Error polling live users:", e);
    }
    return false;
  }, []);

  useLatido(pollLiveUsers, { base: 15000, techo: 90000 });

  // A quien enseña la lista. Cambiarlo recarga SOLO la lista: los KPIs de
  // arriba siguen contando alumnos pase lo que pase, porque mezclarlos es lo
  // que hacia que el panel dijera "1 activo" siendo ese activo el propio admin.
  const [fCuenta, setFCuenta] = useState("alumnos");

  /* ── Carga inicial: lista + KPIs ── */
  useEffect(() => {
    // `activo`: si el filtro cambia (o React desmonta y vuelve a montar) antes
    // de que llegue la respuesta, la vieja no pisa a la nueva.
    let activo = true;
    setLoadingList(true);
    setApiError(null);
    Promise.all([fetchStudentList(fCuenta), fetchGlobalKpis()])
      .then(([list, kpis]) => {
        if (!activo) return;
        setStudents(list);
        setGlobalKpis(kpis);
        if (list.length > 0) {
          setSel(list[0]);          // placeholder ligero para mostrar algo de inmediato
          setSelectedId(list[0].id);
        } else {
          setSel(null);
          setSelectedId(null);
        }
      })
      .catch(e => { if (activo) setApiError(e.message); })
      .finally(() => { if (activo) setLoadingList(false); });
    return () => { activo = false; };
    // Recarga al cambiar de filtro. Solo la LISTA cambia: los KPIs se vuelven
    // a pedir igual y siguen contando alumnos, que es lo correcto.
  }, [fCuenta]);

  /* ── Detalle completo al cambiar alumno seleccionado ── */
  useEffect(() => {
    if (!selectedId) return;
    setLoadingDetail(true);
    fetchStudentDetail(selectedId)
      .then(detail => setSel(detail))
      .catch(e => console.error("Detail fetch error:", e))
      .finally(() => setLoadingDetail(false));
  }, [selectedId]);

  /* ── Sesiones SpaCy al cambiar alumno ── */
  useEffect(() => {
    if (!selectedId) return;
    setSpacySessions([]);
    setArenaSessions([]);
    fetchSpacySessions(selectedId)
      .then(setSpacySessions)
      .catch(e => console.error("SpaCy sessions error:", e));
    // La Arena va aparte y con su propio catch: si falla, las otras dos
    // secciones se siguen viendo. Antes no existia, asi que un alumno que
    // lleva veinte combates aparecia como si no hubiera pisado la Arena.
    fetchArenaSessions(selectedId)
      .then(setArenaSessions)
      .catch(e => console.error("Arena sessions error:", e));
  }, [selectedId]);

  const goTab = (k) => { setMainTab(k); setTabKey(p => p + 1); };
  const pickStu = (s) => {
    setSel(s);
    setSelectedId(s.id);
    setTRes(null);
    goTab("overview");
    setDrawerOpen(false);
  };

  const allLevels = [...new Set(students.map(s => s.level))].sort();
  const filtered = students.filter(s => {
    if (q) {
      const ql = q.toLowerCase();
      const searchable = [
        s.name, s.level, s.country, s.plan,
        s.profile?.email, s.profile?.city, s.profile?.alias,
      ].filter(Boolean).join(" ").toLowerCase();
      if (!searchable.includes(ql)) return false;
    }
    if (fRisk !== "all" && s.risk !== fRisk) return false;
    if (fLevel !== "all" && s.level !== fLevel) return false;
    return true;
  });

  /* ── Loading / Error screens ── */
  if (apiError) return (
    <div style={{ padding: 32, textAlign: "center", color: t.er }}>
      <div style={{ fontSize: 32, marginBottom: 12 }}>⚠️</div>
      <div style={{ fontWeight: 700, marginBottom: 8 }}>Error al cargar el dashboard</div>
      <div style={{ fontSize: F.sm, opacity: .7, marginBottom: 16 }}>{apiError}</div>
      <button onClick={() => window.location.reload()} style={{ padding: "8px 20px", borderRadius: 10, border: `1px solid ${t.er}`, background: "transparent", color: t.er, cursor: "pointer", fontSize: F.sm, fontFamily: "inherit" }}>Reintentar</button>
    </div>
  );

  // ── SIN NADIE A QUIEN ENSEÑAR ──────────────────────────────────────────────
  // La ficha necesita un alumno seleccionado, y la condicion de abajo esperaba
  // a tenerlo. Con la base a ceros (22-09-2026) la lista llega vacia, nunca hay
  // a quien seleccionar y la pestaña se quedaba en «Cargando dashboard…» para
  // siempre: parecia rota. Una lista vacia es una respuesta, no una espera.
  if (!loadingList && students.length === 0) return (
    <div style={{ padding: 40, textAlign: "center", color: t.tx2 }}>
      <div style={{ fontSize: 32, marginBottom: 12 }}>🎓</div>
      <div style={{ fontSize: F.md, fontWeight: 700, color: t.tx, marginBottom: 6 }}>
        {fCuenta === "alumnos" ? "Todavía no hay alumnos" : "No hay cuentas en esta vista"}
      </div>
      <div style={{ fontSize: F.sm, marginBottom: 18 }}>
        {fCuenta === "alumnos"
          ? "En cuanto se registre el primero, aparecerá aquí con su ficha completa."
          : "Prueba con otra vista."}
      </div>
      <div style={{ display: "flex", gap: 8, justifyContent: "center", flexWrap: "wrap" }}>
        {[{ k: "alumnos", l: "🎓 Alumnos" }, { k: "equipo", l: "🛠️ Equipo" }, { k: "todos", l: "Todos" }]
          .filter(o => o.k !== fCuenta)
          .map(o => (
            <button key={o.k} onClick={() => setFCuenta(o.k)} style={{
              padding: "8px 18px", borderRadius: 50, border: "none", cursor: "pointer",
              fontSize: F.sm, fontWeight: 700, fontFamily: "inherit",
              background: t.bg, boxShadow: t.ixs, color: HI,
            }}>Ver {o.l}</button>
          ))}
      </div>
    </div>
  );

  if (loadingList || !sel) return (
    <div style={{ padding: 40, textAlign: "center", color: t.tx2 }}>
      <div style={{ fontSize: 28, marginBottom: 12, animation: "pls 1.5s infinite" }}>📡</div>
      <div style={{ fontSize: F.md, fontWeight: 600 }}>Cargando dashboard…</div>
    </div>
  );

  const runTest = async () => {
    setTR(true); setTRes(null);
    const prompt = `You are SpaCy AI English tutor.\nCreate ONE micro-test:\nStudent: ${sel.name} | Level: ${sel.level}\nTopic: ${sel.currentTopic?.name || "General"}\nGaps: ${(sel.spacy?.gaps || []).join(", ")}\nRespond ONLY in JSON:\n{"question":"...","context":"...","expected":"...","hint":"...","explanation":"...","targets":[]}`;
    try {
      // ✅ CÓDIGO BLINDADO PARA VERCEL
      const resp = await fetch(`${API_BASE}/api/minigames/admin/generate`, { method: "POST", headers: { "Content-Type": "application/json", "Authorization": `Bearer ${localStorage.getItem("token")}` }, body: JSON.stringify({ prompt }) });
      if (!resp.ok) throw new Error();
      const data = await resp.json();
      const raw = typeof data.text === "string" ? data.text : (data.content || []).find(b => b.type === "text")?.text || "{}";
      setTRes(JSON.parse(raw.replace(/```json|```/g, "").trim()));
    } catch {
      setTRes({ question: `[${sel.level}] Practice: "${(sel.spacy?.gaps || ["General"])[0]}"`, context: `Context: ${sel.currentTopic?.name || "General"}`, expected: `Focus: "${(sel.spacy?.gaps || ["General"])[0]}"`, hint: "Review your latest lesson.", explanation: `Gap: "${(sel.spacy?.gaps || ["General"])[0]}"`, targets: sel.spacy?.gaps || [] });
    }
    setTR(false);
  };

  const lc = levelColor(sel.level);
  const risk = riskCfg[sel.risk] || riskCfg.low;
  const xpPct = Math.round(((sel.xp || 0) / (sel.xpMax || 1)) * 100);
  const clC = v => v > 80 ? t.er : v > 60 ? t.wrn : t.ok;
  const sep = dark ? "rgba(255,255,255,.05)" : "rgba(0,0,0,.05)";
  const card = { background: t.bg, borderRadius: 16, padding: bp.xs ? 12 : 16, boxShadow: t.nmd, border: `1px solid ${dark ? "rgba(255,255,255,.06)" : "rgba(0,0,0,.06)"}` };

  const TABS = [
    { k: "overview", l: "Resumen", e: "📊" },
    { k: "progress", l: "Progreso", e: "📋" },
    { k: "skills", l: "Skills", e: "🎯" },
    { k: "spacy", l: "SpaCy IA", e: "🤖" },
  ];

  /* ── PANE: RESUMEN ── */
  const PaneOverview = () => {
    const p = sel.profile || {};
    // ── MIENTRAS LLEGA LA FICHA ──────────────────────────────────────────────
    // Al pulsar un alumno, `sel` pasa a ser la fila LIGERA de la lista, que no
    // trae perfil. Sin esto, la ficha se pintaba entera con guiones durante mas
    // de un segundo y volvia a rellenarse: Rob lo describio como "un lag que
    // recarga la web", y eso es exactamente lo que parece.
    //
    // Un guion significa "no hay dato". Mientras se esta cargando, la verdad es
    // "todavia no se sabe", y son cosas distintas. Se enseña un hueco neutro.
    const cargandoFicha = loadingDetail && !sel.profile;
    const oCargando = (v) => (cargandoFicha ? "···" : v);
    const FLAG = { MX: "🇲🇽", CO: "🇨🇴", IN: "🇮🇳", AE: "🇦🇪", ES: "🇪🇸" };
    const flag = FLAG[sel.country] || "🌍";
    const labelW = bp.xs ? 68 : 84;
    const InfoRow = ({ icon, label, value, mono, accent }) => (
      <div style={{ display: "flex", alignItems: "center", gap: 8, padding: "7px 0", borderBottom: `1px solid ${dark ? "rgba(255,255,255,.04)" : "rgba(0,0,0,.04)"}` }}>
        <span style={{ fontSize: F.md, flexShrink: 0, width: 20, textAlign: "center" }}>{icon}</span>
        <span style={{ fontSize: F.xs, color: t.tx2, width: labelW, flexShrink: 0, textTransform: "uppercase", letterSpacing: ".06em" }}>{label}</span>
        <span style={{ fontSize: F.sm, fontWeight: 600, color: accent || t.tx, fontFamily: mono ? "JetBrains Mono,monospace" : "inherit", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", flex: 1 }}>{value || "—"}</span>
      </div>
    );
    return (
      <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
        <div style={{ background: t.bg, borderRadius: 18, boxShadow: t.nmd, border: `1px solid ${dark ? "rgba(255,255,255,.06)" : "rgba(0,0,0,.06)"}`, overflow: "hidden" }}>
          <div style={{ padding: "14px 18px 12px", borderBottom: `1px solid ${dark ? "rgba(255,255,255,.05)" : "rgba(0,0,0,.05)"}`, display: "flex", alignItems: "center", gap: 10, background: dark ? `linear-gradient(90deg,${lc}0d,transparent)` : `linear-gradient(90deg,${lc}07,transparent)`, flexWrap: "wrap" }}>
            <span style={{ fontSize: F.lg }}>🪪</span>
            <span style={{ fontSize: F.xs, fontWeight: 800, letterSpacing: ".12em", textTransform: "uppercase", color: t.tx2 }}>Perfil del Alumno</span>
            <div style={{ marginLeft: "auto", display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap" }}>
              <Bdg label={`ID #${String(sel.id).padStart(4, "0")}`} color={t.tx2} bg={dark ? "rgba(255,255,255,.06)" : "rgba(0,0,0,.05)"} mono />
              {!bp.xs && p.referral && <Bdg label={p.referral} color={t.pur} />}
            </div>
          </div>
          <div style={{ display: "grid", gridTemplateColumns: bp.xs ? "1fr" : bp.sm ? "1fr" : "1fr 1fr", gap: 0 }}>
            <div style={{ padding: "14px 18px", borderRight: (!bp.xs && !bp.sm) ? `1px solid ${dark ? "rgba(255,255,255,.05)" : "rgba(0,0,0,.05)"}` : "none", borderBottom: (bp.xs || bp.sm) ? `1px solid ${dark ? "rgba(255,255,255,.05)" : "rgba(0,0,0,.05)"}` : "none" }}>
              <div style={{ fontSize: F.xs, fontWeight: 800, letterSpacing: ".12em", textTransform: "uppercase", color: lc, marginBottom: 10 }}>Identidad</div>
              {/* El nombre SI se sabe desde el primer momento —viene en la
                  fila de la lista— asi que se enseña ya: confirma al instante
                  que se pulso a quien se queria. */}
              <InfoRow icon="👤" label="Nombre" value={sel.name} />
              <InfoRow icon="🏷️" label="Alias" value={oCargando(p.alias)} mono accent={HI} />
              <InfoRow icon="📧" label="Email" value={oCargando(p.email)} mono />
              {/* El telefono no se pide en ningun sitio y no hay columna para
                  el en `users`: esta fila no podia tener dato jamas. Se queda
                  fuera hasta que exista donde guardarlo. */}
              <InfoRow icon="📅" label="Registro" value={oCargando(p.joined)} accent={t.ok} />
            </div>
            <div style={{ padding: "14px 18px" }}>
              <div style={{ fontSize: F.xs, fontWeight: 800, letterSpacing: ".12em", textTransform: "uppercase", color: lc, marginBottom: 10 }}>Conexión</div>
              <InfoRow icon={flag} label="País" value={`${sel.country} · ${p.city || "—"}`} />
              <InfoRow icon="🕐" label="Zona" value={oCargando(p.timezone)} />
              {/* ── DISPOSITIVO, NAVEGADOR E IP ────────────────────────────
                  Estas tres filas enseñaban un guion y parecia que faltaba el
                  dato. No falta: NO SE GUARDA, y no por descuido. En models.py
                  hay una linea que lo dice — "last_ip, device_info y
                  browser_info fueron removidos para evitar almacenamiento
                  ilegal de PII" — porque la IP y la huella del dispositivo son
                  datos personales y guardarlos exige base legal.

                  Un guion se lee como "el dato no llego" y manda a buscar un
                  fallo que no existe. Decir el motivo cuesta una linea. */}
              <InfoRow icon="💻" label="Disp." value={p.device || p.sinPII} />
              {!bp.xs && <InfoRow icon="🌐" label="Navegador" value={p.browser || p.sinPII} />}
              <InfoRow icon="🔒" label="IP" value={p.lastIP || p.sinPII} mono={Boolean(p.lastIP)} />
              <InfoRow icon="🗣️" label="Idioma" value={p.lang} mono accent={HI} />
            </div>
          </div>
        </div>

        <div style={{ display: "grid", gridTemplateColumns: bp.xs ? "repeat(2,1fr)" : "repeat(3,1fr)", gap: 10 }}>
          <KpiMini icon="📚" label="Sesiones" value={sel.sessionsTotal} color={HI} t={t} dark={dark} />
          <KpiMini icon="🔥" label="Streak" value={`${sel.streak}d`} color={t.wrn} t={t} dark={dark} />
          <KpiMini icon="⏱" label="Resp." value={`${((sel.speaking?.responseMs || 3000) / 1000).toFixed(1)}s`} color={(sel.speaking?.responseMs || 3000) < 2000 ? t.ok : (sel.speaking?.responseMs || 3000) < 4000 ? t.wrn : t.er} t={t} dark={dark} />
        </div>

        <div style={{ display: "grid", gridTemplateColumns: "1fr", gap: 12 }}>
          <div style={card}>
            <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 10 }}>
              <I.Book s={14} c={HI} />
              <span style={{ fontSize: F.xs, fontWeight: 800, letterSpacing: ".1em", textTransform: "uppercase", color: t.tx2 }}>Lección Actual</span>
              {sel.currentTopic?.unit && <Bdg label={sel.currentTopic.unit} color={HI} />}
            </div>
            <div style={{ fontSize: F.lg, fontWeight: 700, color: t.tx, marginBottom: 3 }}>{sel.currentTopic?.name || "—"}</div>
            <div style={{ fontSize: F.sm, color: t.tx2, marginBottom: 10 }}>{sel.currentTopic?.lesson || "—"}</div>
            <Bar v={sel.currentTopic?.pct || 0} c={`linear-gradient(90deg,${HI}77,${HI})`} h={7} t={t} />
            <div style={{ display: "flex", justifyContent: "space-between", marginTop: 5 }}>
              <span style={{ fontSize: F.xs, color: t.tx2 }}>Progreso</span>
              <span style={{ fontSize: F.sm, fontWeight: 800, fontFamily: "monospace", color: HI }}>{sel.currentTopic?.pct || 0}%</span>
            </div>
          </div>
        </div>
      </div>
    );
  };

  /* ── PANE: PROGRESO ── */
  const PaneProgress = () => (
    <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
      <div style={card}>
        <div style={{ fontSize: F.xs, fontWeight: 800, letterSpacing: ".12em", textTransform: "uppercase", color: t.tx2, marginBottom: 14 }}>Ruta de Aprendizaje</div>
        {(sel.topics || []).map((tp, i) => {
          const cfg = topicCfg[tp.st] || topicCfg.not_started;
          return (
            <div key={i} style={{ display: "flex", alignItems: "flex-start", gap: 12, paddingBottom: i < (sel.topics?.length || 0) - 1 ? 12 : 0, borderBottom: i < (sel.topics?.length || 0) - 1 ? `1px solid ${sep}` : undefined, marginBottom: i < (sel.topics?.length || 0) - 1 ? 12 : 0 }}>
              <div style={{ display: "flex", flexDirection: "column", alignItems: "center", flexShrink: 0 }}>
                <div style={{ width: 26, height: 26, borderRadius: 8, display: "flex", alignItems: "center", justifyContent: "center", background: `${cfg.c}18`, color: cfg.c, fontSize: F.sm, fontWeight: 800, boxShadow: `0 0 0 1px ${cfg.c}33` }}>{cfg.icon}</div>
                {i < (sel.topics?.length || 0) - 1 && <div style={{ width: 1, height: 12, background: sep, marginTop: 3 }} />}
              </div>
              <div style={{ flex: 1, minWidth: 0, marginTop: 3 }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 4 }}>
                  <span style={{ fontSize: F.md, fontWeight: tp.st === "in_progress" ? 700 : 500, color: tp.st === "not_started" ? t.tx2 : t.tx, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", maxWidth: bp.xs ? 160 : 280 }}>{tp.n}</span>
                  {tp.sc > 0 && <span style={{ fontSize: F.md, fontWeight: 800, fontFamily: "monospace", color: cfg.c, flexShrink: 0 }}>{tp.sc}%</span>}
                </div>
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <Bdg label={cfg.l} color={cfg.c} />
                  {tp.sc > 0 && <div style={{ flex: 1, maxWidth: 140 }}><Bar v={tp.sc} c={cfg.c} h={3} t={t} /></div>}
                </div>
              </div>
            </div>
          );
        })}
        {(!sel.topics || sel.topics.length === 0) && (
          <div style={{ textAlign: "center", padding: "20px 0", color: t.tx2, fontSize: F.sm }}>Sin lecciones completadas aún</div>
        )}
      </div>



      {(() => {
        const ORDER = ["A1", "A2", "B1", "B2", "C1", "C2"];
        const LABELS = { A1: "Básico", A2: "Básico Avanzado", B1: "Intermedio", B2: "Interm. Avanzado", C1: "Avanzado", C2: "Dominio" };
        const COLORS = { A1: "#64748b", A2: "#6366f1", B1: "#0891b2", B2: "#059669", C1: "#f59e0b", C2: "#ef4444" };

        return (
          <div style={card}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 14, flexWrap: "wrap", gap: 8 }}>
              <div style={{ fontSize: F.lg, fontWeight: 700, color: t.tx }}>🗺 Mapa CEFR Libre</div>
              <Bdg label={`Nivel Principal: ${sel.level}`} color={lc} />
            </div>
            {ORDER.map((lv, i) => {
              const c = COLORS[lv];
              const isActive = lv === sel.level;

              // Leemos el progreso real del nivel. Si el backend aún no lo manda, usamos el global temporalmente para el nivel activo, o 0.
              const pct = sel.levelProgress ? (sel.levelProgress[lv] || 0) : (isActive ? sel.progress : 0);
              const isDone = pct === 100;

              return (
                <div key={lv} style={{ display: "flex", alignItems: "center", gap: bp.xs ? 8 : 12, padding: "10px 12px", background: isActive ? (dark ? `${c}10` : `${c}08`) : "transparent", borderRadius: isActive ? 12 : 0, borderBottom: i < ORDER.length - 1 ? `1px solid ${sep}` : undefined }}>
                  {/* Círculo del Nivel (Sin candados, sin gris) */}
                  <div style={{ width: 38, height: 38, borderRadius: 11, flexShrink: 0, display: "flex", alignItems: "center", justifyContent: "center", fontSize: F.sm, fontWeight: 900, background: isDone ? `${c}22` : isActive ? `${c}28` : dark ? "rgba(255,255,255,.04)" : "rgba(0,0,0,.04)", boxShadow: isActive ? `${t.nxs},0 0 0 2px ${c}55` : t.ixs, color: c }}>
                    {lv}
                  </div>

                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 5, gap: 4 }}>
                      <div style={{ display: "flex", alignItems: "center", gap: 6, minWidth: 0 }}>
                        <span style={{ fontSize: F.md, fontWeight: isActive ? 700 : 500, color: t.tx, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{bp.xs ? lv : LABELS[lv]}</span>
                        {isActive && !bp.xs && <Bdg label="ACTIVO" color={c} />}
                        {isDone && !bp.xs && <Bdg label="✓" color={t.ok} />}
                      </div>
                      {/* Porcentaje en lugar de guion */}
                      <span style={{ fontSize: F.sm, fontWeight: 800, fontFamily: "monospace", color: c, flexShrink: 0 }}>{pct}%</span>
                    </div>
                    {/* Barra de progreso habilitada para todos */}
                    <div style={{ height: 5, borderRadius: 3, overflow: "hidden", boxShadow: t.ixs, background: dark ? "rgba(255,255,255,.06)" : "rgba(0,0,0,.06)" }}>
                      <div style={{ width: `${pct}%`, height: "100%", borderRadius: 3, background: `linear-gradient(90deg,${c}66,${c})`, boxShadow: isActive && dark ? `0 0 6px ${c}88` : "", transition: "width .8s ease" }} />
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        );
      })()}
    </div>
  );

  /* ── PANE: SKILLS ── */
  const PaneSkills = () => {
    // 1. Mapeo de datos para los componentes del Dashboard
    const total_study_seconds = sel.habit ? Math.round(sel.habit.monthHours * 3600) : 0;

    // Mapeamos los ejercicios completados a la estructura esperada por StatsGrid
    const completedCount = Math.round((sel.progress || 0) / 10); // Estimación rápida
    const mockLessonsProgress = Array(completedCount).fill({ percentage: 100 });

    const summaryData = {
      total_xp: sel.xp || 0,
      current_streak: sel.streak || 0,
      best_streak: sel.streak || 0,
      weekly_xp_change: (sel.activity || []).reduce((a, b) => a + b.s, 0) * 10,
      weekly_lessons_completed: (sel.activity || []).reduce((a, b) => a + b.s, 0),
      weekly_study_seconds: (sel.habit?.avgDailyMin || 0) * 60 * 7,
      lessons_progress: mockLessonsProgress,
      skill_scores: sel.dashboard_skill_scores || [],
      study_time_breakdown: sel.dashboard_study_time || []
    };

    const monthlyActivity = (sel.monthlyActivity || []).map(a => ({
      date: a.date,
      count: a.count
    }));

    // Reutilizamos la función de formato de StatsGrid
    const formatStudyTime = (seconds) => {
      if (!seconds) return "0m";
      const hours = Math.floor(seconds / 3600);
      const minutes = Math.floor((seconds % 3600) / 60);
      if (hours > 0) return `${hours}h ${minutes}m`;
      return `${minutes}m`;
    };

    return (
      <div className="crm-shield-dp" style={{ display: "flex", flexDirection: "column", gap: "24px" }}>

        {/* Usamos el componente directo del dashboard para mantener consistencia */}
        <StatsGrid summaryData={summaryData} totalStudyTime={formatStudyTime(total_study_seconds)} />

        {/* Gráfico de Actividad Mensual (adaptado para 7 días en el CRM) */}
        <ActivityChartCard
          activityData={monthlyActivity}
          customAccentColor={HI}
          customTextColor={t.tx2}
          customGridColor={sep}
        />

        {/* Gráficos Avanzados: Radar y Doughnut */}
        <div style={{ display: "grid", gridTemplateColumns: bp.sm ? "1fr" : "1fr 1fr", gap: "16px" }}>
          <div style={{ ...card, padding: "24px" }}>
            <h2 style={{ fontSize: "1.2rem", fontWeight: "bold", marginBottom: "20px", color: t.tx }}>Radar de Habilidades</h2>
            <div style={{ height: "300px", position: "relative" }}>
              <SkillsRadarChart
                skillScores={summaryData.skill_scores}
                customAccentColor={HI}
                customTextColor={t.tx2}
              />
            </div>
          </div>
          <div style={{ ...card, padding: "24px" }}>
            <h2 style={{ fontSize: "1.2rem", fontWeight: "bold", marginBottom: "20px", color: t.tx }}>Tiempo por Habilidad</h2>
            <div style={{ height: "300px", position: "relative" }}>
              <StudyTimeDoughnutChart studyTimeBreakdown={summaryData.study_time_breakdown} />
            </div>
          </div>
        </div>

        {/* Bloque original de Speaking Fluency */}
        <div style={card}>
          <div style={{ padding: "10px 14px", borderRadius: 11, background: t.bg, boxShadow: t.ixs, border: `1px solid ${sep}`, display: "flex", justifyContent: "space-between", alignItems: "center", gap: 10 }}>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: F.md, color: t.tx, fontWeight: 600 }}>⏱ Resp. Speaking</div>
              <div style={{ fontSize: F.sm, color: t.tx2, marginTop: 3 }}>{(sel.speaking?.responseMs || 3000) < 1500 ? "🟢 Fluido — near-native" : (sel.speaking?.responseMs || 3000) < 3500 ? "🟡 Normal — mejorando" : "🔴 Alta latencia"}</div>
            </div>
            <span style={{ fontSize: bp.xs ? F["2xl"] : F["3xl"], fontWeight: 900, fontFamily: "monospace", color: (sel.speaking?.responseMs || 3000) < 1500 ? t.ok : (sel.speaking?.responseMs || 3000) < 3500 ? t.wrn : t.er, flexShrink: 0 }}>{((sel.speaking?.responseMs || 3000) / 1000).toFixed(1)}s</span>
          </div>
        </div>
      </div>
    );
  };

  /* ── PANE: SPACY IA ── */
  const PaneSpacy = () => (
    <div style={{ display: "flex", flexDirection: "column", gap: 14, minHeight: "80vh" }}>


      <div style={card}>
        <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 14, flexWrap: "wrap" }}>
          <div style={{ width: 30, height: 30, borderRadius: 9, background: `linear-gradient(135deg,${HI},#c084fc)`, display: "flex", alignItems: "center", justifyContent: "center", fontSize: F.lg, boxShadow: dark ? `0 0 10px ${HI}44` : "", flexShrink: 0 }}>🤖</div>
          <span style={{ fontSize: F.lg, fontWeight: 700, color: t.tx }}>Historial de Sesiones</span>
          {sel.spacyLive && <div style={{ marginLeft: "auto", display: "flex", alignItems: "center", gap: 5, padding: "4px 10px", borderRadius: 50, background: t.bg, boxShadow: t.ixs }}><div style={{ width: 7, height: 7, borderRadius: "50%", background: "#10b981", boxShadow: dark ? "0 0 6px #10b981" : "", animation: "pls 2s infinite" }} /><span style={{ fontSize: F.xs, fontWeight: 700, color: "#10b981" }}>En vivo</span></div>}
        </div>
        {loadingDetail
          ? <div style={{ textAlign: "center", padding: "20px 0", color: t.tx2, fontSize: F.sm }}>Cargando sesiones…</div>
          : <SpacySessionPanel sel={sel} t={t} HI={HI} dark={dark} sessions={spacySessions} arena={arenaSessions} />
        }
      </div>
    </div>
  );



  // Traduce {teacher: 1, superadmin: 1} a "1 profesor y 1 administrador".
  const otrasCuentas = (() => {
    const NOMBRES = { teacher: ["profesor", "profesores"], superadmin: ["administrador", "administradores"], admin: ["administrador", "administradores"] };
    const trozos = Object.entries(globalKpis.otherAccounts || {})
      .filter(([, n]) => n > 0)
      .map(([tipo, n]) => {
        const [uno, varios] = NOMBRES[tipo] || [tipo, tipo];
        return `${n} ${n === 1 ? uno : varios}`;
      });
    if (!trozos.length) return null;
    return trozos.length === 1 ? trozos[0] : trozos.slice(0, -1).join(", ") + " y " + trozos.slice(-1);
  })();

  /* ── SIDEBAR CONTENT ── */
  const SidebarContent = () => (
    <>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 12 }}>
        <span style={{ fontSize: F.lg, fontWeight: 700, color: t.tx }}>Alumnos</span>
        <div style={{ display: "flex", alignItems: "center", gap: 5, padding: "3px 9px", borderRadius: 50, background: t.bg, boxShadow: t.ixs, border: `1px solid ${sep}` }}>
          <div style={{ width: 6, height: 6, borderRadius: "50%", background: t.ok, boxShadow: dark ? `0 0 5px ${t.ok}` : "", animation: "pls 2s infinite" }} />
          <span style={{ fontSize: F.xs, fontWeight: 700, color: t.ok }}>{students.filter(s => s.spacyLive).length} live</span>
        </div>
      </div>

      {/* ── QUIEN NO SALE EN ESTA LISTA, Y POR QUE ────────────────────────────
          La lista enseña SOLO alumnos, y tiene que seguir asi: meter aqui al
          administrador es lo que hacia que el panel dijera "1 activo" cuando el
          unico activo era Rob mirando su propio panel.

          Pero al no decirlo en ningun sitio, el efecto es que las cuentas
          parecen haberse borrado. Paso de verdad: tras limpiar las de prueba
          quedaban tres cuentas, la lista enseñaba una, y eso se lee como un
          fallo grave en lugar de como un filtro. Filtrar esta bien; filtrar en
          silencio, no. */}
      {otrasCuentas && (
        <div style={{ fontSize: F.xs, color: t.tx2, marginBottom: 10, lineHeight: 1.5 }}>
          Solo alumnos. {otrasCuentas} sin contar aqui.
        </div>
      )}

      <div style={{ display: "flex", alignItems: "center", gap: 7, padding: "8px 11px", borderRadius: 10, marginBottom: 10, background: t.bg, boxShadow: sFocus ? `${t.ism},0 0 0 1.5px ${HI}44` : t.ism, border: `1px solid ${sFocus ? HI + "44" : sep}`, transition: "all .2s" }}>
        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke={sFocus ? HI : t.tx2} strokeWidth="2.5" strokeLinecap="round" style={{ flexShrink: 0 }}><circle cx="11" cy="11" r="8" /><path d="m21 21-4.35-4.35" /></svg>
        <input type="text" placeholder="Buscar…" value={q} onChange={e => setQ(e.target.value)} onFocus={() => setSFocus(true)} onBlur={() => setSFocus(false)} style={{ flex: 1, background: "transparent", border: "none", outline: "none", fontSize: F.sm, color: t.tx, fontFamily: "inherit" }} />
        {q && <button onClick={() => setQ("")} style={{ background: "transparent", border: "none", cursor: "pointer", color: t.tx2, fontSize: F.md }}>✕</button>}
      </div>

      {/* ── QUE CUENTAS SE ENSEÑAN ────────────────────────────────────────
          Por defecto alumnos, que es lo que la pantalla dice ser. "Equipo" y
          "Todos" existen porque el administrador tambien quiere verse a si
          mismo y ver a sus profesores, y hasta ahora esas cuentas no aparecian
          en ningun sitio del panel.

          Cambiar esto NO toca los numeros de arriba: siguen contando solo
          alumnos. Meter al administrador en la media de fluidez o en "activos
          hoy" seria mentirse a uno mismo con su propio uso de prueba. */}
      <div style={{ display: "flex", gap: 4, marginBottom: 8, flexWrap: "wrap" }}>
        {[{ k: "alumnos", l: "🎓 Alumnos" }, { k: "equipo", l: "🛠️ Equipo" }, { k: "todos", l: "Todos" }].map(o => {
          const activo = fCuenta === o.k;
          return (
            <button key={o.k} onClick={() => setFCuenta(o.k)} style={{
              padding: "3px 9px", borderRadius: 50, border: "none", cursor: "pointer",
              fontSize: F.xs, fontWeight: activo ? 800 : 500, fontFamily: "inherit",
              background: activo ? `${HI}18` : t.bg,
              boxShadow: activo ? `${t.ixs},0 0 0 1.5px ${HI}55` : t.ixs,
              color: activo ? HI : t.tx2, transition: "all .15s", whiteSpace: "nowrap",
            }}>{o.l}</button>
          );
        })}
      </div>

      <div style={{ overflowX: "auto", paddingBottom: 3, marginBottom: 10 }}>
        <div style={{ display: "flex", gap: 4, width: "max-content" }}>
          {["all", ...allLevels].map(lv => { const a = fLevel === lv, c = lv === "all" ? HI : levelColor(lv); return <button key={lv} onClick={() => setFLevel(lv)} style={{ padding: "3px 9px", borderRadius: 50, border: "none", cursor: "pointer", fontSize: F.xs, fontWeight: a ? 800 : 500, fontFamily: "inherit", background: a ? `${c}18` : t.bg, boxShadow: a ? `${t.ixs},0 0 0 1.5px ${c}55` : t.ixs, color: a ? c : t.tx2, transition: "all .15s", whiteSpace: "nowrap" }}>{lv === "all" ? "Todos" : lv}</button>; })}
        </div>
      </div>

      <div style={{ display: "flex", gap: 4, marginBottom: 12, flexWrap: "wrap" }}>
        {[{ k: "all", l: "Todos" }, { k: "low", l: "🟢 Bajo" }, { k: "medium", l: "🟡 Medio" }, { k: "critical", l: "🔴 Crítico" }].map(r => { const a = fRisk === r.k, c = r.k === "all" ? HI : (riskCfg[r.k]?.c || HI); return <button key={r.k} onClick={() => setFRisk(r.k)} style={{ padding: "3px 9px", borderRadius: 50, border: "none", cursor: "pointer", fontSize: F.xs, fontWeight: a ? 700 : 500, fontFamily: "inherit", background: a ? `${c}14` : t.bg, boxShadow: a ? `${t.ixs},0 0 0 1px ${c}44` : t.ixs, color: a ? c : t.tx2, transition: "all .15s" }}>{r.l}</button>; })}
      </div>

      <div style={{ display: "flex", flexDirection: "column", gap: 4, maxHeight: "calc(100vh - 440px)", overflowY: "auto" }}>
        {filtered.map(s => {
          const isSel = sel?.id === s.id, slc = levelColor(s.level), r = riskCfg[s.risk] || riskCfg.low;
          return (
            <div key={s.id} onClick={() => pickStu(s)} onMouseEnter={() => setHovId(s.id)} onMouseLeave={() => setHovId(null)}
              style={{ display: "flex", alignItems: "center", gap: 10, padding: "10px 11px", borderRadius: 12, cursor: "pointer", transition: "all .15s", background: isSel ? `${HI}0c` : hovId === s.id ? (dark ? "rgba(255,255,255,.02)" : "rgba(0,0,0,.02)") : "transparent", borderLeft: `2.5px solid ${isSel ? HI : "transparent"}` }}>
              <div style={{ position: "relative", flexShrink: 0 }}>
                <div style={{ width: 36, height: 36, borderRadius: 10, display: "flex", alignItems: "center", justifyContent: "center", fontSize: F.xs, fontWeight: 800, background: `${slc}18`, color: slc, boxShadow: `0 0 0 1px ${isSel ? HI : slc}33`, textShadow: dark ? `0 0 6px ${slc}` : "" }}>{s.ini}</div>
                <div style={{ position: "absolute", bottom: -2, right: -2, fontSize: F["2xs"], fontWeight: 800, padding: "0 4px", borderRadius: 50, background: slc, color: "#fff", lineHeight: "13px" }}>{s.level}</div>
                {s.spacyLive && <div style={{ position: "absolute", top: -2, right: -2, width: 8, height: 8, borderRadius: "50%", background: t.ok, boxShadow: dark ? `0 0 5px ${t.ok}` : "", animation: "pls 2s infinite" }} />}
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <span style={{ fontSize: F.sm, fontWeight: isSel ? 700 : 500, color: isSel ? HI : t.tx, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", display: "block", marginBottom: 3 }}>{s.name}</span>
                <div style={{ display: "flex", alignItems: "center", gap: 5, marginBottom: 4 }}>
                  <span style={{ fontSize: F.xs, fontWeight: 700, padding: "1px 5px", borderRadius: 50, background: `${r.c}14`, color: r.c }}>{r.l}</span>
                  <span style={{ fontSize: F.xs, color: t.tx2 }}>{s.lastSeen}</span>
                </div>
                <Bar v={s.progress} c={`linear-gradient(90deg,${slc}66,${slc})`} h={3} t={t} />
              </div>
            </div>
          );
        })}
        {filtered.length === 0 && !loadingList && (
          <div style={{ textAlign: "center", padding: "20px 0", color: t.tx2, fontSize: F.sm }}>Sin resultados</div>
        )}
      </div>
    </>
  );

  /* ── ROOT RENDER ── */
  return (
    <div style={{ color: t.tx, fontFamily: "system-ui,-apple-system,sans-serif", display: "flex", flexDirection: "column", gap: 14 }}>

      {/* Mobile Drawer */}
      {bp.md && drawerOpen && (
        <div className="neu-drawer-backdrop">
          <div className="neu-drawer-bg" onClick={() => setDrawerOpen(false)} />
          <div className="neu-drawer-panel" style={{ width: bp.sm ? "88vw" : 300 }}>
            <div className="neu-drawer-header">
              <span style={{ fontSize: F.lg, fontWeight: 800, color: "var(--tx)" }}>Seleccionar Alumno</span>
              <button className="neu-drawer-close" onClick={() => setDrawerOpen(false)}>
                <I.Close s={16} c="currentColor" />
              </button>
            </div>
            {SidebarContent()}
          </div>
        </div>
      )}

      {/* KPI Strip */}
      <div style={{ display: "grid", gridTemplateColumns: bp.sm ? "repeat(4,1fr)" : bp.md ? "repeat(4,1fr)" : "repeat(8,1fr)", gap: bp.xs ? 7 : 10 }}>
        {[
          { l: "Alumnos", v: globalKpis.totalStudents, c: HI, icon: "👥" },
          { l: "Activos", v: globalKpis.activeToday, c: t.ok, icon: "📡" },
          // Un guion cuando todavia no hay ni una nota. Un 0 se lee como "van
          // fatal" y es justo lo contrario de la verdad, que es "aun no se sabe".
          { l: "Fluidez", v: globalKpis.avgFluency == null ? "—" : `${globalKpis.avgFluency}%`, c: t.pur, icon: "💬" },
          { l: "Interv.", v: globalKpis.interventions, c: t.wrn, icon: "⚡" },
          { l: "Riesgo", v: globalKpis.churnRisk, c: t.er, icon: "⚠️" },
          // Aqui habia "Level-Up" y no se podia arreglar: NO EXISTE de donde
          // sacarlo —ni tabla de historial de niveles ni columna de nivel en
          // `users`—, asi que el recuadro solo podia estar vacio o mentir.
          // Ocupa su sitio el alta de alumnos del mes, que si es un dato real.
          { l: "Nuevos", v: globalKpis.newStudentsThisMonth, c: "#f59e0b", icon: "🚀" },
          { l: "Conv.hrs", v: globalKpis.convHours, c: t.blu, icon: "🎙️" },
          // El MRR lleva debajo el dinero en riesgo —lo que suman las
          // suscripciones en `past_due`—, porque los dos numeros solo
          // significan algo juntos: "$0 activos" es una noticia muy distinta
          // segun haya $0 o $1.398 a punto de caerse. Y ocupa la misma casilla.
          { l: "MRR", v: globalKpis.mrrTotal, c: t.ok, icon: "💰",
            sub: globalKpis.mrrEnRiesgo && globalKpis.mrrEnRiesgo !== "$0"
                 ? `${globalKpis.mrrEnRiesgo} en riesgo` : null },
        ].map(k => (
          <KpiMini key={k.l} icon={k.icon} label={k.l} value={k.v} color={k.c} t={t} dark={dark} sub={k.sub} />
        ))}
      </div>

      {/* 🔥 INYECTAMOS EL RADAR AQUÍ 🔥 */}
      <LiveUsersPanel users={liveUsers} t={t} HI={HI} dark={dark} bp={bp} />

      {/* Mobile student bar */}
      {bp.md && (
        <div style={{ background: t.bg, borderRadius: 16, padding: "10px 12px", boxShadow: t.nmd, border: `1px solid ${sep}`, display: "flex", alignItems: "center", gap: 10 }}>
          <button className="neu-btn" onClick={() => setDrawerOpen(true)} style={{ display: "flex", alignItems: "center", gap: 6, padding: "8px 12px", borderRadius: 11, flexShrink: 0 }}>
            <I.Menu s={15} c="currentColor" />
            {!bp.xs && <span style={{ fontSize: F.sm }}>Alumnos</span>}
          </button>
          <div style={{ flex: 1, overflowX: "auto", display: "flex", gap: 6, alignItems: "center", paddingBottom: 2, WebkitOverflowScrolling: "touch" }}>
            {students.map(s => {
              const slc = levelColor(s.level), isSel = sel?.id === s.id;
              return (
                <button key={s.id} onClick={() => pickStu(s)} title={s.name} style={{ position: "relative", flexShrink: 0, width: bp.xs ? 34 : 38, height: bp.xs ? 34 : 38, borderRadius: 10, display: "flex", alignItems: "center", justifyContent: "center", fontSize: F.xs, fontWeight: 800, background: `${slc}18`, color: slc, border: `2px solid ${isSel ? HI : "transparent"}`, boxShadow: isSel ? `0 0 0 1px ${HI}55` : t.ixs, cursor: "pointer", transition: "all .15s" }}>
                  {s.ini}
                  <div style={{ position: "absolute", bottom: -3, right: -3, fontSize: F["2xs"], fontWeight: 800, padding: "0 3px", borderRadius: 50, background: slc, color: "#fff", lineHeight: "12px" }}>{s.level}</div>
                  {s.spacyLive && <div style={{ position: "absolute", top: -2, left: -2, width: 7, height: 7, borderRadius: "50%", background: t.ok, animation: "pls 2s infinite" }} />}
                </button>
              );
            })}
          </div>
          <div style={{ flexShrink: 0, textAlign: "right", minWidth: 0 }}>
            <div style={{ fontSize: bp.xs ? F.sm : F.md, fontWeight: 700, color: t.tx, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", maxWidth: bp.xs ? 60 : 100 }}>{sel?.name?.split(" ")[0] || "—"}</div>
            <div style={{ fontSize: F.xs, color: lc, fontWeight: 800 }}>{sel?.level || "—"}</div>
          </div>
        </div>
      )}

      {/* Two-panel layout */}
      <div style={{ display: "flex", gap: 16, alignItems: "flex-start" }}>

        {/* Sidebar — desktop only */}
        {!bp.md && (
          <div style={{ width: 240, flexShrink: 0, position: "sticky", top: 0 }}>
            <div style={{ background: t.bg, borderRadius: 20, padding: 16, boxShadow: t.nmd, border: `1px solid ${dark ? "rgba(255,255,255,.06)" : "rgba(0,0,0,.06)"}` }}>
              {SidebarContent()}
            </div>
          </div>
        )}

        {/* Main panel */}
        <div style={{ flex: 1, display: "flex", flexDirection: "column", gap: 12, minWidth: 0 }}>

          {/* Hero Banner */}
          <div style={{ borderRadius: bp.xs ? 16 : 22, overflow: "hidden", position: "relative", background: dark ? `linear-gradient(135deg,${lc}18 0%,${t.bg} 60%)` : `linear-gradient(135deg,${lc}10 0%,${t.bg} 65%)`, border: `1px solid ${lc}33`, boxShadow: t.nmd }}>
            <div style={{ position: "absolute", top: -30, left: -20, width: 180, height: 180, borderRadius: "50%", background: lc, opacity: .06, filter: "blur(44px)", pointerEvents: "none" }} />
            <div style={{ padding: bp.xs ? "12px 14px" : "16px 20px", display: "flex", alignItems: "center", gap: bp.xs ? 10 : 16, position: "relative" }}>
              <Ring pct={xpPct} size={bp.xs ? 52 : 68} stroke={bp.xs ? 5 : 6} color={lc} glow={dark} bg={dark ? "rgba(255,255,255,.06)" : "rgba(0,0,0,.06)"}>
                <div style={{ textAlign: "center" }}>
                  <div style={{ fontSize: bp.xs ? F.sm : F.md, fontWeight: 900, color: lc, lineHeight: 1, textShadow: dark ? `0 0 10px ${lc}` : "" }}>{sel.ini}</div>
                  <div style={{ fontSize: F["2xs"], color: t.tx2 }}>{sel.level}</div>
                </div>
              </Ring>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: bp.xs ? F.lg : F["2xl"], fontWeight: 900, color: t.tx, letterSpacing: "-.02em", marginBottom: 6, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: bp.xs ? "nowrap" : "normal" }}>{sel.name}</div>
                <div style={{ display: "flex", gap: 5, flexWrap: "wrap", alignItems: "center" }}>
                  <Bdg label={`📍 ${sel.country}`} color={t.tx2} bg={dark ? "rgba(255,255,255,.06)" : "rgba(0,0,0,.05)"} />
                  {!bp.xs && <Bdg label={sel.plan} color={HI} />}
                  <Bdg label={risk.l} color={risk.c} />
                  {sel.spacyLive && <Bdg label="⚡ Sesión" color={t.ok} />}
                  {loadingDetail && <Bdg label="↻ sync" color={t.tx2} />}
                </div>
              </div>
              {!bp.xs && (
                <div style={{ display: "flex", gap: bp.sm ? 5 : 8, alignItems: "center", flexShrink: 0 }}>
                  {[{ v: `${sel.streak || 0}d`, l: "streak", c: t.wrn, icon: "🔥" }, { v: sel.sessionsTotal || 0, l: "sesiones", c: HI, icon: "📚" }, { v: `${sel.habit?.retention || 0}%`, l: "retención", c: t.ok, icon: "🧠" }].map(m => (
                    <div key={m.l} style={{ textAlign: "center", padding: bp.sm ? "7px 8px" : "9px 12px", borderRadius: 12, background: dark ? "rgba(0,0,0,.2)" : "rgba(0,0,0,.04)", boxShadow: t.ixs }}>
                      <div style={{ fontSize: F.md, marginBottom: 2 }}>{m.icon}</div>
                      <div style={{ fontSize: bp.sm ? F.lg : F.xl, fontWeight: 900, color: m.c, fontFamily: "monospace", lineHeight: 1, textShadow: dark ? `0 0 8px ${m.c}44` : "" }}>{m.v}</div>
                      <div style={{ fontSize: F["2xs"], color: t.tx2, textTransform: "uppercase", letterSpacing: ".06em", marginTop: 2 }}>{m.l}</div>
                    </div>
                  ))}
                </div>
              )}
            </div>
            <div style={{ padding: bp.xs ? "0 14px 12px" : "0 20px 14px", display: "flex", alignItems: "center", gap: 10 }}>
              <span style={{ fontSize: F.sm, color: t.tx2, flexShrink: 0 }}>{(sel.xp || 0).toLocaleString()} XP</span>
              <div style={{ flex: 1, height: 5, borderRadius: 3, overflow: "hidden", background: dark ? "rgba(255,255,255,.07)" : "rgba(0,0,0,.07)" }}>
                <div style={{ width: `${xpPct}%`, height: "100%", background: `linear-gradient(90deg,${lc}77,${lc})`, borderRadius: 3, boxShadow: dark ? `0 0 6px ${lc}66` : "", transition: "width .8s ease" }} />
              </div>
              <span style={{ fontSize: F.sm, color: lc, fontWeight: 800, fontFamily: "monospace", flexShrink: 0 }}>{xpPct}%</span>
            </div>
          </div>

          {/* Tab Bar */}
          <div style={{ background: t.bg, borderRadius: 16, padding: "5px 6px", boxShadow: t.nmd, border: `1px solid ${dark ? "rgba(255,255,255,.06)" : "rgba(0,0,0,.06)"}`, display: "flex", gap: 3 }}>
            {TABS.map(tab => {
              const a = mainTab === tab.k;
              return (
                <button key={tab.k} onClick={() => goTab(tab.k)}
                  style={{ flex: 1, padding: bp.xs ? "8px 4px" : "10px 6px", borderRadius: 11, border: "none", cursor: "pointer", fontFamily: "inherit", fontSize: F.sm, fontWeight: a ? 700 : 500, transition: "all .18s", background: a ? `${lc}14` : "transparent", color: a ? lc : t.tx2, boxShadow: a ? `${t.nxs},0 0 0 1.5px ${lc}44` : "none", display: "flex", alignItems: "center", justifyContent: "center", gap: 4, flexDirection: bp.xs ? "column" : "row", whiteSpace: "nowrap" }}>
                  <span style={{ fontSize: bp.xs ? F.xl : F.lg }}>{tab.e}</span>
                  {!bp.xs && <span style={{ overflow: "hidden", textOverflow: "ellipsis", fontSize: bp.sm ? F.xs : F.sm }}>{tab.l}</span>}
                  {a && !bp.xs && <div style={{ width: 5, height: 5, borderRadius: "50%", background: lc, boxShadow: dark ? `0 0 4px ${lc}` : "", flexShrink: 0 }} />}
                </button>
              );
            })}
          </div>

          {/* Tab Content */}
          <div key={`${sel.id}-${tabKey}`} style={{ animation: "fadeUp .2s ease" }}>
            {mainTab === "overview" && PaneOverview()}
            {mainTab === "progress" && PaneProgress()}
            {mainTab === "skills" && PaneSkills()}
            {mainTab === "spacy" && PaneSpacy()}
          </div>
        </div>
      </div>
      <style>{`
        .crm-shield-dp .dp-neumorphic {
            background: ${t.bg} !important;
            color: ${t.tx} !important;
            box-shadow: ${t.ixs} !important;
            border: 1px solid ${sep} !important;
        }
        .crm-shield-dp .dp-neumorphic:hover {
            box-shadow: ${t.nxs} !important;
            transform: translateY(-2px) !important;
        }
        .crm-shield-dp .dp-section-title {
            color: ${t.tx} !important;
        }
        .crm-shield-dp .dp-stat-title {
            color: ${t.tx2} !important;
        }
        .crm-shield-dp .dp-stat-value {
            color: ${t.tx} !important;
        }
      `}</style>
      <div style={{ height: 20 }} />
    </div>
  );
}